import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Paper, Tab, Tabs } from '@material-ui/core';

import { financeApi as api } from 'api/finance';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';
import dayjs from 'utils/dayjsConfig';

function PaymentModal({ onClose, open, paymentItem, setNotification }) {
    if (!paymentItem || !paymentItem.id) {
        return null;
    }

    const maxDate = dayjs(new Date()).format('YYYY-MM-DD');
    const formFields = [
        {
            name: 'amount',
            inputElement: 'textField',
            label: 'Amount',
            gridValue: 6,
            inputProps: { required: true, type: 'number' },
        },
        {
            name: 'datePaid',
            inputElement: 'date',
            label: 'Date Paid',
            gridValue: 6,
            inputProps: { required: true, max: maxDate },
        },
        { name: 'notes', inputElement: 'textField', label: 'Notes', inputProps: { multiline: true } },
    ];

    const payItem = async (formData, resetForm) => {
        let res;
        if (paymentItem.type === 'purchase order') {
            res = await api.payPurchaseOrder(paymentItem.id, formData);
        } else if (paymentItem.type === 'truck') {
            res = await api.payTruck(paymentItem.id, formData);
        }

        if (!res.status) {
            setNotification({ text: `Failed to pay ${paymentItem.type}. ${res.msg}`, level: 'error' });
            return false;
        }

        resetForm();
        paymentItem.onSuccess();
        onClose();
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
            <DialogTitle>
                {paymentItem.type === 'purchase order'
                    ? `Pay Purchase Order ${paymentItem.id}`
                    : `Pay Truck ${paymentItem.id}`}
            </DialogTitle>
            <DialogContent>
                <FormComponent formFields={formFields} onSubmit={payItem} />
            </DialogContent>
        </Dialog>
    );
}

function Payments() {
    const [selectedTab, setSelectedTab] = useState('0');
    const [notification, setNotification] = useState({ level: null, text: null });

    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentItem, setPaymentItem] = useState(null);

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    const openPaymentModal = (id, type, onSuccess) => {
        setPaymentItem({ id, type, onSuccess });
        setPaymentModalOpen(true);
    };

    return (
        <Paper>
            <Notifications options={notification} />
            <PaymentModal
                setNotification={setNotification}
                paymentItem={paymentItem}
                open={paymentModalOpen}
                onClose={() => {
                    setPaymentItem(null);
                    setPaymentModalOpen(false);
                }}
            />
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab label="Purchase Orders" value="0" />
                <Tab label="Inbound Trucks" value="1" />
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <PurchaseOrderPayments setNotification={setNotification} openPaymentModal={openPaymentModal} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <InboundTrucksPayments setNotification={setNotification} openPaymentModal={openPaymentModal} />
            </TabPanel>
        </Paper>
    );
}

function PurchaseOrderPayments({ openPaymentModal, setNotification }) {
    const [purchaseOrders, setPurchaseOrders] = useState([]);

    const getPurchaseOrders = async () => {
        const res = await api.getPurchaseOrdersWithPaymentInfo();
        if (!res.status) {
            setNotification({ level: 'error', text: `Failed to get purchase orders. ${res.msg}` });
            return;
        }

        const pos = (res.data?.rows ?? []).map((item) => {
            item.createdAt = dayjs(item.createdAt.date).format('MM/DD/YYYY HH:mm a');
            item.scheduledDeliveryDate = dayjs(item.scheduledDeliveryDate.date).format('MM/DD/YYYY');
            item.datePaid = item.datePaid ? dayjs(item.datePaid.date).format('MM/DD/YYYY') : null;
            return item;
        });

        setPurchaseOrders(pos);
    };

    useEffect(() => {
        getPurchaseOrders();
    }, []);

    const columns = [
        { accessor: 'poID', Header: 'Purchase Order ID' },
        { accessor: 'createdAt', Header: 'Created At' },
        { accessor: 'scheduledDeliveryDate', Header: 'Scheduled Delivery' },
        { accessor: 'vendorName', Header: 'Vendor' },
        { accessor: 'facilityName', Header: 'Facility' },
        { accessor: 'status', Header: 'Status' },
        { accessor: 'datePaid', Header: 'Date Paid' },
        { accessor: 'amount', Header: 'Amount Paid' },
        { accessor: 'paidBy', Header: 'Paid By' },
        { accessor: 'notes', Header: 'Notes' },
    ];

    return (
        <div>
            <DataTable columns={columns} data={purchaseOrders} />
        </div>
    );
}

function InboundTrucksPayments({ openPaymentModal, setNotification }) {
    const [trucks, setTrucks] = useState([]);

    const getInboundTrucks = async () => {
        const res = await api.getTrucksWithPaymentInfo();
        if (!res.status) {
            setNotification({ level: 'error', text: `Failed to get inbound trucks. ${res.msg}` });
            return;
        }

        const fobTrucks = (res.data?.rows ?? []).map((item) => {
            item.createdAt = dayjs(item.createdAt.date).format('MM/DD/YYYY HH:mm a');
            item.datePaid = item.datePaid ? dayjs(item.datePaid.date).format('MM/DD/YYYY') : null;
            return item;
        });

        setTrucks(fobTrucks);
    };

    useEffect(() => {
        getInboundTrucks();
    }, []);

    const columns = [
        { accessor: 'truckID', Header: 'Truck ID' },
        { accessor: 'createdAt', Header: 'Created At' },
        { accessor: 'carrierName', Header: 'Carrier' },
        { accessor: 'status', Header: 'Status' },
        { accessor: 'datePaid', Header: 'Date Paid' },
        { accessor: 'amount', Header: 'Amount Paid' },
        { accessor: 'paidBy', Header: 'Paid By' },
        { accessor: 'notes', Header: 'Notes' },
        {
            Header: 'Pay',
            Cell: ({ row }) => {
                if (row.original.status === 'PLACED') {
                    return (
                        <Button
                            onClick={async () => {
                                openPaymentModal(row.original.truckID, 'truck', getInboundTrucks);
                            }}
                        >
                            PAY
                        </Button>
                    );
                }
                return null;
            },
        },
    ];

    return (
        <div>
            <DataTable columns={columns} data={trucks} />
        </div>
    );
}

export { Payments };
